import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getAddressOverview, getAddressTransactions, getAddressTokenTransfers,
  getAddressNativeTransfers, getAddressDexTrades, getAddressPortfolio,
  getAddressLabel, upsertAddressLabel, deleteAddressLabel,
  getAddressWatchStatus, watchAddress, unwatchAddress,
} from '../lib/api';
import { truncateAddress, copyToClipboard } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AddressBadge, TxHashBadge, getKnownAddressInfo } from '../components/AddressBadge';
import { ShareToTelegram } from '../components/ShareToTelegram';
import {
  ArrowLeft, Copy, Check, Wallet, Coins, ArrowDownLeft, ArrowUpRight,
  ArrowLeftRight, BarChart3, Clock, Activity, DollarSign, Tag,
  ChevronLeft, ChevronRight, ChevronDown, RefreshCw, Pencil, Trash2, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';

const PAGE_SIZE = 25;

// ---------- helpers ----------
function fmtUsd(v, compact = false) {
  const n = parseFloat(String(v ?? 0).replace(/,/g, ''));
  if (!isFinite(n)) return '$0.00';
  if (compact && Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (compact && Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  if (Math.abs(n) < 0.01 && n !== 0) return `$${n.toFixed(6)}`;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtNum(v, dp = 6) {
  const n = parseFloat(String(v ?? 0).replace(/,/g, ''));
  if (!isFinite(n)) return '0';
  if (Math.abs(n) >= 1_000_000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (Math.abs(n) >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
  return n.toFixed(dp);
}
function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diffSec = Math.max(0, (now - d.getTime()) / 1000);
    if (diffSec < 60) return `${Math.floor(diffSec)}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}
function fmtTimeFull(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { hour12: false }); } catch { return iso; }
}

// ---------- main ----------
export default function AddressPage() {
  const { address } = useParams();
  const normalized = address?.toLowerCase();

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState('transactions');
  const [watch, setWatch] = useState({ watched: false, loading: false });

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [d, w] = await Promise.all([
        getAddressOverview(normalized),
        getAddressWatchStatus(normalized).catch(() => ({ watched: false })),
      ]);
      setOverview(d);
      setWatch({ watched: !!w.watched, loading: false, ...w });
    } catch (e) {
      toast.error('Failed to load address overview');
    } finally {
      setLoading(false);
    }
  }, [normalized]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const onToggleWatch = async () => {
    setWatch(w => ({ ...w, loading: true }));
    try {
      if (watch.watched) {
        await unwatchAddress(normalized);
        toast.success('Stopped watching this wallet');
      } else {
        const label = overview?.label?.label || null;
        await watchAddress(normalized, label ? { label } : {});
        toast.success('Now watching — alerts via Telegram bot');
      }
      const w = await getAddressWatchStatus(normalized);
      setWatch({ watched: !!w.watched, loading: false, ...w });
    } catch (e) {
      toast.error('Failed to toggle watch');
      setWatch(w => ({ ...w, loading: false }));
    }
  };

  const onCopyAddress = async () => {
    await copyToClipboard(normalized);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const knownLocal = getKnownAddressInfo(normalized);
  const labelText = overview?.label?.label || knownLocal?.label || null;
  const labelSource = overview?.label?.source || (knownLocal ? 'system' : null);
  const labelTags = overview?.label?.tags || [];
  const labelCategory = overview?.label?.category || null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="address-overview-page">
      {/* Top bar */}
      <div className="border-b border-slate-800 bg-slate-900/40 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Link to="/" className="text-slate-400 hover:text-slate-100 flex-shrink-0" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider text-slate-500">Address Explorer</div>
              <div className="flex items-center gap-2 font-mono text-sm">
                <span className="text-slate-200 truncate">{normalized}</span>
                <button
                  onClick={onCopyAddress}
                  className="text-slate-400 hover:text-amber-400 transition-colors flex-shrink-0"
                  data-testid="copy-address-btn"
                  aria-label="Copy address"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button
              size="sm"
              variant={watch.watched ? 'default' : 'outline'}
              onClick={onToggleWatch}
              disabled={watch.loading}
              data-testid="watch-toggle-btn"
              className={watch.watched
                ? 'bg-emerald-700 hover:bg-emerald-600 text-white'
                : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
            >
              {watch.watched
                ? <><Eye className="w-4 h-4 mr-1" /> Watching</>
                : <><EyeOff className="w-4 h-4 mr-1" /> Watch</>}
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={fetchOverview}
              disabled={loading}
              data-testid="refresh-address-btn"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <ShareToTelegram
              kind="addr"
              payload={normalized}
              label="Share"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Header section */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-5 sm:p-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-700/10 border border-amber-500/30 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  {loading ? <Skeleton className="h-6 w-48" /> : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-xl sm:text-2xl font-semibold text-slate-100" data-testid="address-label-display">
                        {labelText || 'Wallet'}
                      </h1>
                      {labelSource === 'system' && (
                        <Badge variant="outline" className="border-blue-700/50 text-blue-300 text-xs">SYSTEM</Badge>
                      )}
                      {labelSource && labelSource !== 'system' && (
                        <Badge variant="outline" className="border-emerald-700/50 text-emerald-300 text-xs">CUSTOM</Badge>
                      )}
                      {labelCategory && (
                        <Badge variant="outline" className="border-amber-700/50 text-amber-300 text-xs">{labelCategory}</Badge>
                      )}
                    </div>
                  )}
                  {!loading && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      First seen: {fmtTimeFull(overview?.first_seen)} · Last seen: {fmtTimeFull(overview?.last_seen)}
                    </div>
                  )}
                </div>
              </div>

              {labelTags.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="address-tags">
                  {labelTags.map(t => (
                    <Badge key={t} variant="secondary" className="bg-slate-800 text-slate-300 text-xs">#{t}</Badge>
                  ))}
                </div>
              )}

              {/* Balance Summary Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                <StatTile
                  testid="stat-native-krown"
                  icon={<Coins className="w-4 h-4 text-amber-400" />}
                  label="Native KROWN"
                  value={loading ? '—' : fmtNum(overview?.native_balance?.formatted)}
                  sub="KROWN"
                />
                <StatTile
                  testid="stat-transfers-count"
                  icon={<ArrowLeftRight className="w-4 h-4 text-violet-400" />}
                  label="Token Transfers"
                  value={loading ? '—' : (overview?.counts?.transfers ?? 0).toLocaleString()}
                  sub="ERC20"
                />
                <StatTile
                  testid="stat-native-transfers-count"
                  icon={<ArrowDownLeft className="w-4 h-4 text-emerald-400" />}
                  label="Native Transfers"
                  value={loading ? '—' : (overview?.counts?.native_transfers ?? 0).toLocaleString()}
                  sub="On-chain"
                />
                <StatTile
                  testid="stat-trades-count"
                  icon={<BarChart3 className="w-4 h-4 text-blue-400" />}
                  label="DEX Trades"
                  value={loading ? '—' : (overview?.counts?.dex_trades ?? 0).toLocaleString()}
                  sub="Swaps"
                />
              </div>

              {/* Token Balances */}
              {!loading && overview?.balances?.length > 0 && (
                <div className="border-t border-slate-800 pt-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Balances</div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {overview.balances.map((b, i) => (
                      <div
                        key={`${b.symbol}-${i}`}
                        className="flex items-center justify-between bg-slate-800/40 border border-slate-800 rounded-lg px-3 py-2"
                        data-testid={`balance-row-${b.symbol.toLowerCase()}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="text-xs text-slate-400">{b.symbol}</div>
                          {b.type === 'native' && (
                            <Badge variant="outline" className="border-amber-700/50 text-amber-300 text-[10px] px-1.5">NATIVE</Badge>
                          )}
                        </div>
                        <div className="text-sm font-mono text-slate-100">{fmtNum(b.formatted)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-slate-900/60 border border-slate-800 p-1 flex w-full overflow-x-auto" data-testid="address-tabs-list">
            <TabsTrigger value="transactions" data-testid="tab-transactions"><Activity className="w-4 h-4 mr-1" />All Txs</TabsTrigger>
            <TabsTrigger value="token-transfers" data-testid="tab-token-transfers"><ArrowLeftRight className="w-4 h-4 mr-1" />Tokens</TabsTrigger>
            <TabsTrigger value="native-transfers" data-testid="tab-native-transfers"><ArrowDownLeft className="w-4 h-4 mr-1" />Native</TabsTrigger>
            <TabsTrigger value="dex-trades" data-testid="tab-dex-trades"><BarChart3 className="w-4 h-4 mr-1" />DEX</TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio"><DollarSign className="w-4 h-4 mr-1" />Portfolio</TabsTrigger>
            <TabsTrigger value="labels" data-testid="tab-labels"><Tag className="w-4 h-4 mr-1" />Labels</TabsTrigger>
          </TabsList>

          <TabsContent value="transactions"><TxList address={normalized} type="all" /></TabsContent>
          <TabsContent value="token-transfers"><TxList address={normalized} type="token-transfers" /></TabsContent>
          <TabsContent value="native-transfers"><TxList address={normalized} type="native-transfers" /></TabsContent>
          <TabsContent value="dex-trades"><TxList address={normalized} type="dex-trades" /></TabsContent>
          <TabsContent value="portfolio"><PortfolioTab address={normalized} /></TabsContent>
          <TabsContent value="labels"><LabelsTab address={normalized} overview={overview} onChange={fetchOverview} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatTile({ icon, label, value, sub, testid }) {
  return (
    <div className="bg-slate-800/40 border border-slate-800 rounded-lg p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">{icon}<span>{label}</span></div>
      <div className="text-lg font-mono text-slate-100">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{sub}</div>
    </div>
  );
}

// ---------- TxList (handles all 4 listing tabs) ----------
// For type === 'all' we use CURSOR-based pagination (block_time `before`) so
// the unified stream is unbounded. Other tabs stay on page-based pagination
// since each source already supports it natively.
function TxList({ address, type }) {
  const isCursor = type === 'all';
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState([]);   // cursor mode: array of pages appended
  const [nextCursor, setNextCursor] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Reset state when switching tab or address
  useEffect(() => {
    setPage(1); setPages([]); setNextCursor(null); setData(null);
  }, [address, type]);

  const fetcher = useCallback(async () => {
    setLoading(true);
    try {
      if (isCursor) {
        const res = await getAddressTransactions(address, { pageSize: PAGE_SIZE });
        setPages([res.rows || []]);
        setNextCursor(res.next_cursor || null);
        setData(res);
      } else {
        let res;
        if (type === 'token-transfers') res = await getAddressTokenTransfers(address, page, PAGE_SIZE);
        else if (type === 'native-transfers') res = await getAddressNativeTransfers(address, page, PAGE_SIZE);
        else if (type === 'dex-trades') res = await getAddressDexTrades(address, page, PAGE_SIZE);
        setData(res);
      }
    } catch (e) {
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [address, type, page, isCursor]);

  useEffect(() => { fetcher(); }, [fetcher]);

  const onLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await getAddressTransactions(address, { pageSize: PAGE_SIZE, before: nextCursor });
      setPages(p => [...p, res.rows || []]);
      setNextCursor(res.next_cursor || null);
    } catch (e) {
      toast.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rows = isCursor ? pages.flat() : (data?.rows || []);

  return (
    <Card className="bg-slate-900/60 border-slate-800 mt-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-slate-300">
            {type === 'all' && 'All Transactions'}
            {type === 'token-transfers' && 'Token Transfers (ERC20)'}
            {type === 'native-transfers' && 'Native Transfers (KROWN)'}
            {type === 'dex-trades' && 'DEX Trades'}
            <span className="text-xs text-slate-500 ml-2">
              {isCursor
                ? (rows.length > 0 ? `${rows.length}${nextCursor ? '+' : ''} loaded` : '0 loaded')
                : `${total.toLocaleString()} results`}
            </span>
          </CardTitle>
          {!isCursor && (
            <div className="flex items-center gap-1">
              <Button
                size="sm" variant="ghost"
                disabled={page <= 1 || loading}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                data-testid="prev-page-btn"
                className="h-7 px-2 text-slate-400 hover:text-slate-100"
              ><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs text-slate-400 min-w-[60px] text-center" data-testid="page-indicator">
                {page} / {totalPages}
              </span>
              <Button
                size="sm" variant="ghost"
                disabled={page >= totalPages || loading}
                onClick={() => setPage(p => p + 1)}
                data-testid="next-page-btn"
                className="h-7 px-2 text-slate-400 hover:text-slate-100"
              ><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 space-y-2">
            <Skeleton className="h-8 w-full bg-slate-800" />
            <Skeleton className="h-8 w-full bg-slate-800" />
            <Skeleton className="h-8 w-full bg-slate-800" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No activity to show.</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto" data-testid="tx-list-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-800/40 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-800">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Type</th>
                    <th className="text-left px-4 py-2.5 font-medium">Tx</th>
                    <th className="text-left px-4 py-2.5 font-medium">From / To</th>
                    <th className="text-right px-4 py-2.5 font-medium">Amount</th>
                    <th className="text-right px-4 py-2.5 font-medium">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => <TxRow key={`${r.tx_hash}-${r.log_index ?? 'n'}-${i}`} row={r} type={type} />)}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-800/60" data-testid="tx-list-cards">
              {rows.map((r, i) => <TxCard key={`${r.tx_hash}-${r.log_index ?? 'n'}-${i}`} row={r} type={type} />)}
            </div>
          </>
        )}
        {/* Cursor "Load More" footer (only for unified All Txs tab) */}
        {isCursor && !loading && rows.length > 0 && (
          <div className="border-t border-slate-800 p-3 flex items-center justify-center" data-testid="load-more-section">
            {nextCursor ? (
              <Button
                size="sm" variant="outline"
                onClick={onLoadMore}
                disabled={loadingMore}
                data-testid="load-more-btn"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                {loadingMore ? (
                  <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Loading…</>
                ) : (
                  <><ChevronDown className="w-4 h-4 mr-1.5" /> Load older transactions</>
                )}
              </Button>
            ) : (
              <span className="text-xs text-slate-500" data-testid="end-of-list">End of activity history</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Shared display logic for both the desktop table row and the mobile card.
function rowDisplay(row, type) {
  const kind = row._kind || (type === 'dex-trades' ? 'dex_swap' : type === 'native-transfers' ? 'native_transfer' : type === 'token-transfers' ? 'erc20_transfer' : 'unknown');
  const direction = row.direction || (row.side === 'BUY' ? 'in' : row.side === 'SELL' ? 'out' : null);

  let badgeKind, label;
  if (kind === 'dex_swap' || row.is_krown_pool) {
    const side = row.side || row.kind || (row.krown_direction === 'out' ? 'BUY' : 'SELL');
    badgeKind = side === 'BUY' ? 'buy' : 'sell';
    label = side;
  } else if (kind === 'native_transfer') {
    badgeKind = 'native'; label = 'NATIVE';
  } else {
    badgeKind = 'erc20'; label = 'ERC20';
  }

  let amount = '—';
  let amountSub = '';
  if (kind === 'dex_swap' || row.is_krown_pool) {
    amount = `${fmtNum(row.krown_amount)} KROWN`;
    amountSub = row.volume_usd != null ? fmtUsd(row.volume_usd, true) : '';
  } else if (kind === 'native_transfer') {
    amount = `${fmtNum(row.value_krown ?? row.amount_formatted)} KROWN`;
  } else {
    amount = `${row.amount_formatted ?? '0'}`;
    amountSub = row.token_symbol || '';
  }
  return { kind, direction, badgeKind, label, amount, amountSub };
}

function TypeBadge({ badgeKind, label }) {
  const cls = badgeKind === 'buy'
    ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700/50'
    : badgeKind === 'sell'
      ? 'bg-rose-900/40 text-rose-300 border-rose-700/50'
      : badgeKind === 'native'
        ? 'bg-amber-900/30 text-amber-300 border-amber-700/50'
        : 'bg-violet-900/30 text-violet-300 border-violet-700/50';
  return <Badge className={`text-[10px] px-1.5 ${cls}`}>{label}</Badge>;
}

function DirectionArrow({ direction }) {
  if (direction === 'in') return <ArrowDownLeft className="w-3 h-3 text-emerald-400" />;
  if (direction === 'out') return <ArrowUpRight className="w-3 h-3 text-rose-400" />;
  return null;
}

// Desktop table row
function TxRow({ row, type }) {
  const { direction, badgeKind, label, amount, amountSub } = rowDisplay(row, type);
  return (
    <tr className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
      <td className="px-4 py-2.5 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <TypeBadge badgeKind={badgeKind} label={label} />
          <DirectionArrow direction={direction} />
        </div>
      </td>
      <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">
        <TxHashBadge hash={row.tx_hash} />
      </td>
      <td className="px-4 py-2.5 text-xs whitespace-nowrap">
        <div className="flex flex-col gap-0.5">
          {row.from_address && (
            <div className="flex items-center gap-1">
              <span className="text-slate-500 w-6">From:</span>
              <AddressBadge address={row.from_address} truncate />
            </div>
          )}
          {row.to_address && (
            <div className="flex items-center gap-1">
              <span className="text-slate-500 w-6">To:</span>
              <AddressBadge address={row.to_address} truncate />
            </div>
          )}
          {!row.from_address && !row.to_address && row.user_wallet && (
            <AddressBadge address={row.user_wallet} truncate />
          )}
        </div>
      </td>
      <td className="px-4 py-2.5 text-right font-mono text-xs whitespace-nowrap">
        <div className="text-slate-200">{amount}</div>
        {amountSub && <div className="text-slate-500 text-[10px]">{amountSub}</div>}
      </td>
      <td className="px-4 py-2.5 text-right text-xs text-slate-400 whitespace-nowrap" title={fmtTimeFull(row.block_time)}>
        <Clock className="w-3 h-3 inline mr-0.5 -mt-0.5" />{fmtTime(row.block_time)}
      </td>
    </tr>
  );
}

// Mobile stacked card — avoids the unreadable character-by-character wrapping
// that a 5-column table produces on a ~390px viewport.
function TxCard({ row, type }) {
  const { direction, badgeKind, label, amount, amountSub } = rowDisplay(row, type);
  return (
    <div className="px-4 py-3 space-y-2" data-testid="tx-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <TypeBadge badgeKind={badgeKind} label={label} />
          <DirectionArrow direction={direction} />
          <span className="text-[11px] text-slate-500" title={fmtTimeFull(row.block_time)}>
            <Clock className="w-3 h-3 inline mr-0.5 -mt-0.5" />{fmtTime(row.block_time)}
          </span>
        </div>
        <div className="text-right">
          <div className="text-sm font-mono text-slate-100">{amount}</div>
          {amountSub && <div className="text-[10px] text-slate-500">{amountSub}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-500 w-12 flex-shrink-0">Tx</span>
        <div className="min-w-0 truncate"><TxHashBadge hash={row.tx_hash} /></div>
      </div>
      {row.from_address && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500 w-12 flex-shrink-0">From</span>
          <div className="min-w-0 truncate"><AddressBadge address={row.from_address} truncate /></div>
        </div>
      )}
      {row.to_address && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500 w-12 flex-shrink-0">To</span>
          <div className="min-w-0 truncate"><AddressBadge address={row.to_address} truncate /></div>
        </div>
      )}
      {!row.from_address && !row.to_address && row.user_wallet && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500 w-12 flex-shrink-0">Wallet</span>
          <div className="min-w-0 truncate"><AddressBadge address={row.user_wallet} truncate /></div>
        </div>
      )}
    </div>
  );
}

// ---------- Portfolio Tab ----------
function PortfolioTab({ address }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const d = await getAddressPortfolio(address);
        if (alive) setData(d);
      } catch (e) {
        toast.error('Failed to load portfolio');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [address]);

  return (
    <Card className="bg-slate-900/60 border-slate-800 mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm text-slate-300 flex items-center justify-between">
          <span>Portfolio (Live Spot)</span>
          {!loading && data && (
            <span className="text-base text-amber-400 font-mono" data-testid="portfolio-total-usd">{fmtUsd(data.total_value_usd)}</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 space-y-2">
            <Skeleton className="h-8 w-full bg-slate-800" />
            <Skeleton className="h-8 w-full bg-slate-800" />
          </div>
        ) : !data || data.holdings.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No token holdings.</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto" data-testid="portfolio-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-800/40 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-800">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Token</th>
                    <th className="text-right px-4 py-2.5 font-medium">Balance</th>
                    <th className="text-right px-4 py-2.5 font-medium">Price</th>
                    <th className="text-right px-4 py-2.5 font-medium">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.holdings.map((h, i) => (
                    <tr key={`${h.symbol}-${i}`} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`portfolio-row-${h.symbol.toLowerCase()}`}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-200 font-medium">{h.symbol}</span>
                          <span className="text-slate-500 text-xs">{h.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-200">{fmtNum(h.formatted)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-400">{fmtUsd(h.price_usd)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-amber-400">{fmtUsd(h.value_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-800/60" data-testid="portfolio-cards">
              {data.holdings.map((h, i) => (
                <div key={`${h.symbol}-${i}`} className="px-4 py-3" data-testid={`portfolio-card-${h.symbol.toLowerCase()}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-slate-200 font-medium">{h.symbol}</div>
                      {h.name && <div className="text-slate-500 text-xs truncate">{h.name}</div>}
                    </div>
                    <div className="text-amber-400 font-mono text-sm flex-shrink-0">{fmtUsd(h.value_usd)}</div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1.5 text-xs">
                    <span className="text-slate-500">Balance <span className="font-mono text-slate-300">{fmtNum(h.formatted)}</span></span>
                    <span className="text-slate-500">Price <span className="font-mono text-slate-300">{fmtUsd(h.price_usd)}</span></span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Labels Tab ----------
function LabelsTab({ address, overview, onChange }) {
  const [label, setLabel] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLabel(overview?.label?.label || '');
    setTagsInput((overview?.label?.tags || []).join(', '));
    setCategory(overview?.label?.category || '');
  }, [overview]);

  const canEdit = overview?.label?.source !== 'system';
  const isCustom = overview?.label?.source && overview?.label?.source !== 'system';

  const onSave = async () => {
    if (!label.trim()) { toast.error('Label is required'); return; }
    setSaving(true);
    try {
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
      await upsertAddressLabel({ address, label: label.trim(), tags, category: category.trim() || null });
      toast.success('Label saved');
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save label');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isCustom) return;
    setSaving(true);
    try {
      await deleteAddressLabel(address);
      setLabel(''); setTagsInput(''); setCategory('');
      toast.success('Label removed');
      onChange?.();
    } catch (e) {
      toast.error('Failed to delete label');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-slate-900/60 border-slate-800 mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
          <Tag className="w-4 h-4" /> Custom Label / Tags
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canEdit ? (
          <div className="bg-blue-950/30 border border-blue-800/50 rounded-lg p-3 text-xs text-blue-300">
            This is a SYSTEM-known address ({overview?.label?.label}). System labels can't be overridden, but you can still annotate it with tags/category.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Label</label>
              <Input
                value={label} onChange={e => setLabel(e.target.value)}
                placeholder="e.g. Whale Wallet" maxLength={60}
                className="bg-slate-800 border-slate-700 text-slate-100"
                data-testid="label-input"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Category</label>
              <Input
                value={category} onChange={e => setCategory(e.target.value)}
                placeholder="e.g. individual, exchange, contract" maxLength={30}
                className="bg-slate-800 border-slate-700 text-slate-100"
                data-testid="category-input"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <label className="text-xs text-slate-500">Tags (comma-separated)</label>
              <Input
                value={tagsInput} onChange={e => setTagsInput(e.target.value)}
                placeholder="e.g. whale, vesting, mev"
                className="bg-slate-800 border-slate-700 text-slate-100"
                data-testid="tags-input"
              />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Button onClick={onSave} disabled={saving || !label.trim()} data-testid="save-label-btn" className="bg-amber-600 hover:bg-amber-500 text-slate-950">
                <Pencil className="w-4 h-4 mr-1" />Save
              </Button>
              {isCustom && (
                <Button onClick={onDelete} disabled={saving} variant="outline" data-testid="delete-label-btn" className="border-rose-800/50 text-rose-300 hover:bg-rose-900/30">
                  <Trash2 className="w-4 h-4 mr-1" />Remove
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
